import { useState, useRef, useEffect, useCallback, type FormEvent } from 'react';
import {
  Send, Loader2, AlertTriangle, CheckCircle2, RotateCcw, LifeBuoy,
  Download, ArrowRight, Mic, Flag, MoreVertical, History,
} from 'lucide-react';
import { BottomSheet, playSpeechStream, MarkdownText } from '@evi/ui';
import { EvieOrb } from '@/components/primitives';
import {
  sendAssistantMessage, synthesizeDriverSpeech, downloadSessionsReportPdf,
  confirmAssistantAction,
  type AssistantToolCall,
} from '@/lib/api';
import { normalizeLocale, localeToBcp47 } from '@evi/shared';
import i18n from '@/lib/i18n';
import { useAuthStore } from '@/store/auth';
import { usePinnedShortcuts } from '@/hooks/usePinnedShortcuts';
import { useEvieVoiceStore } from '@/store/evieVoice';
import { useEvieChatStore, type MessageEntry } from '@/store/evieChat';
import { useTourStore } from '@/store/tour';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import { useTranslation } from 'react-i18next';
import styles from './DriverAssistSheet.module.css';

/**
 * Sandboxed driver-side AI chat.
 *
 * Mounted on PreSessionPage (and active-charging page later) as a
 * floating "Need help?" button — see PreSessionPage.tsx where the
 * companion `DriverAssistFab` lives. Tapping opens this sheet on
 * top of the existing StickyCTA.
 *
 * The agent is scoped by the server-side system prompt
 * (apps/api/src/routes/ai/driver-assist.ts:SYSTEM_PROMPT). When a
 * request falls outside scope the server declines and sets
 * `suggested_action: 'contact_support'`; this component shows the
 * Contact-support button off that flag alone, no client text match.
 * Copy varies by `surface` ('charge-start' on the charge screens,
 * 'general' elsewhere) and is resolved through the `driverAssist` i18n
 * namespace (keyed by surface); QUICK_ACTION_KEYS below maps each surface to
 * its quick-action keys.
 *
 * Conversation state lives client-side in the evieChat store (lifted
 * out of this component so it survives navigation — the single sheet
 * instance is mounted at the AppShell level). The server keeps its own
 * copy keyed on `conversation_id` for 1h, so we just pass the same id
 * with every message. No history-resend from the client. After each
 * assistant reply the thread is snapshotted into the store's per-user
 * capped-3 recent memory (the admin EvieChat pattern).
 *
 * Tool calls render as inline status pills ("Checking charger
 * status...", "Soft reset issued") between user/assistant bubbles.
 * The PWA gives drivers a sense of what the agent is doing so a
 * stalled response doesn't feel like a hang.
 */

type Surface = 'charge-start' | 'general' | 'store';

// Per-surface quick-action keys. 'charge-start' is the charge screens
// (PreSession, Charging); 'general' is the map-launched assistant; 'store' the
// shop. All presentational copy (greeting, scope hint, placeholder,
// quick-action labels/prompts, FAB labels, chrome) is resolved through the
// `driverAssist` i18n namespace inside the components — only the surface ->
// quick-action-key mapping lives here. The server prompt covers all surfaces,
// so only the copy varies.
const QUICK_ACTION_KEYS: Record<Surface, readonly [string, string]> = {
  'charge-start': ['wontStart', 'checkStatus'],
  general: ['startCharge', 'rewards'],
  store: ['whichCharger', 'setupAcUltra'],
};

// Empty-reply fallback (server returned an empty assistant message) is
// t('fallback.emptyReply'); shown only in that edge case.
// A scope/permission refusal from the server (ErrorCode.FORBIDDEN) is NOT a
// failure — the driver asked for data outside their own (network-wide, another
// user, admin-only). Render it as a normal EVII message in her voice
// (t('fallback.scopeRefusal')), not the red error block. Genuine failures
// (network, 500, budget) keep the error block.

// MessageEntry moved to @/store/evieChat (the store holds the thread).

/** Stable empty list for the history selector — a fresh [] every render
 *  would defeat zustand's referential-equality bailout (the admin store's
 *  EMPTY_INQUIRIES pattern). */
const EMPTY_CONVERSATIONS: never[] = [];

interface DriverAssistSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Session id when the driver is on PreSessionPage with a pending
   *  session row, OR on ChargingPage with an active one. Optional —
   *  PreSession can also pass just charger_id. */
  sessionId?: string;
  chargerId?: string;
  /** Callback to surface contact-support routing with prefilled
   *  context. Receives the session/charger so the parent can build
   *  a deep link or open a mail/help-desk form pre-populated. */
  onContactSupport: () => void;
  /** App navigation. EVII's suggest_navigation tool surfaces an allowlisted
   *  path on the envelope; the navigation button calls this to route there.
   *  The route change unmounts the host and this sheet, which is the
   *  "close on navigate" behavior. */
  onNavigate: (path: string) => void;
  /** Selects the copy bundle. 'charge-start' (default) on the charge
   *  screens; 'general' for the map-launched assistant. */
  surface?: Surface;
}

export function DriverAssistSheet({
  isOpen,
  onClose,
  sessionId,
  chargerId,
  onContactSupport,
  onNavigate,
  surface = 'charge-start',
}: DriverAssistSheetProps) {
  const { t } = useTranslation('driverAssist');
  // Compact relative time for the history rows ("just now", "5m ago",
  // "3h ago", "2d ago"). Inline so it can reach the i18n `t`.
  const relativeTime = (at: number): string => {
    const mins = Math.floor((Date.now() - at) / 60_000);
    if (mins < 1) return t('time.justNow');
    if (mins < 60) return t('time.minutesAgo', { n: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('time.hoursAgo', { n: hours });
    return t('time.daysAgo', { n: Math.floor(hours / 24) });
  };
  // Quick-action chips for this surface, resolved from the i18n namespace.
  const quickActions = QUICK_ACTION_KEYS[surface].map((key) => ({
    label: t(`quick.${key}.label`),
    prompt: t(`quick.${key}.prompt`),
  }));
  // Conversation id + transcript live in the evieChat store so the thread
  // survives navigation (the store mints the id; loading a stored
  // conversation from the history memory swaps both).
  const conversationId = useEvieChatStore((s) => s.conversationId);
  const messages = useEvieChatStore((s) => s.messages);
  const setMessages = useEvieChatStore((s) => s.setMessages);
  const recordSnapshot = useEvieChatStore((s) => s.recordSnapshot);
  const activateConversation = useEvieChatStore((s) => s.activateConversation);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // History panel (three-dot in the sheet header): the capped-3 recent
  // conversations + suggested prompts. Closes with the sheet.
  const [historyOpen, setHistoryOpen] = useState(false);
  useEffect(() => { if (!isOpen) setHistoryOpen(false); }, [isOpen]);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // ── Voice mode state ──
  const userId = useAuthStore((s) => s.user?._id ?? null);
  // EVII's intro reads who the driver is to the network and their three
  // pins (frictionless flow, piece 5) — both already on /auth/me, so the
  // greeting is templated here and costs no read and no model call.
  const firstName = useAuthStore((s) => (s.user?.name ?? '').trim().split(/\s+/)[0] ?? '');
  const { persona, pins } = usePinnedShortcuts();
  const { t: tShortcuts } = useTranslation('shortcuts');
  const voiceActive = useEvieVoiceStore((s) => s.voiceActive);
  const setVoiceActive = useEvieVoiceStore((s) => s.setVoiceActive);
  const toggleVoice = useEvieVoiceStore((s) => s.toggleVoice);
  // The driver's persisted voice (female | male), default female. Sent to the
  // speak endpoint, which maps it to the Polly voice (Tiffany / Matthew).
  const voiceChoice = useEvieVoiceStore(
    (s) => (userId ? s.voiceByUser[userId] ?? 'female' : 'female'),
  );
  // Mic auto-start on open (per-user, persisted; default on).
  const micAutostart = useEvieVoiceStore(
    (s) => (userId ? s.micAutostartByUser[userId] ?? true : true),
  );
  const [speaking, setSpeaking] = useState(false);

  // Recent conversations for the history panel — the per-user capped-3
  // memory from the evieChat store (persisted, keyed by user._id).
  const recentConversations = useEvieChatStore(
    (s) => (userId ? s.conversationsByUser[userId] ?? EMPTY_CONVERSATIONS : EMPTY_CONVERSATIONS),
  );
  // Suggested prompts: a light heuristic over the stored conversations'
  // first messages — no model calls, the admin Recent-chips pattern.
  // Deduped case-insensitively, capped at 3.
  const suggestedPrompts = Array.from(
    new Map(
      recentConversations.map((c) => [
        c.first_user_text.trim().toLowerCase(),
        c.first_user_text.trim(),
      ]),
    ).values(),
  ).slice(0, 3);

  // Greet on first open. One-shot — closing + reopening keeps the
  // existing thread. On the general surface the greeting is personal:
  // the driver's first name, their persona (homeowner / fleet / driver)
  // and their three pins, named in the locale's own list form. Returning
  // drivers (a conversation already in the per-user memory) get the
  // shorter welcome-back. The charge-start and store surfaces keep their
  // task greetings; a driver with no name on file gets the generic line.
  useEffect(() => {
    if (!isOpen) return;
    if (messages.length > 0) return;
    const personalGreeting = (): string => {
      if (surface !== 'general' || !firstName) return t(`greeting.${surface}`);
      const labels = pins.map((p) => tShortcuts(p.labelKey));
      const pinList = (() => {
        try {
          return new Intl.ListFormat(localeToBcp47(normalizeLocale(i18n.language)), { style: 'long', type: 'conjunction' }).format(labels);
        } catch {
          return labels.join(', ');
        }
      })();
      const returning = recentConversations.length > 0;
      const key = returning ? 'greeting.personal.returning' : `greeting.personal.${persona}`;
      return t(key, { name: firstName, pins: pinList });
    };
    setMessages([
      {
        kind: 'assistant',
        timestamp: Date.now(),
        text: personalGreeting(),
        tool_calls: [],
        suggested_action: null,
      },
    ]);
  }, [isOpen, messages.length]);

  // Auto-scroll to bottom on new message.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const handleSend = useCallback(
    async (text: string): Promise<string | null> => {
      const trimmed = text.trim();
      if (!trimmed || sending) return null;
      setMessages((prev) => [
        ...prev,
        { kind: 'user', text: trimmed, timestamp: Date.now() },
      ]);
      setInput('');
      setSending(true);
      try {
        const res = await sendAssistantMessage({
          message: trimmed,
          conversation_id: conversationId,
          surface,
          locale: normalizeLocale(i18n.language),
          ...(sessionId ? { session_id: sessionId } : {}),
          ...(chargerId ? { charger_id: chargerId } : {}),
        });
        const answer = res.assistant_message || t('fallback.emptyReply');
        setMessages((prev) => [
          ...prev,
          {
            kind: 'assistant',
            text: answer,
            tool_calls: res.tool_calls_made,
            suggested_action: res.suggested_action,
            receipt_download: res.receipt_download ?? null,
            navigation: res.navigation ?? null,
            pending_action: res.pending_action ?? null,
            timestamp: Date.now(),
          },
        ]);
        // Snapshot into the per-user capped-3 memory (zustand set is
        // synchronous, so the store already holds the appended reply).
        recordSnapshot(userId);
        return answer;
      } catch (err) {
        // ky throws HTTPError on 4xx/5xx. We surface a stripped
        // message — the underlying response body is the
        // `{success:false, error:{code,message}}` envelope.
        let message = t('error.generic');
        let retryable = true;
        let scopeRefusal = false;
        if (err instanceof Error && 'response' in err) {
          const httpErr = err as { response: Response };
          try {
            const body = await httpErr.response.json();
            const code: string | undefined = body?.error?.code;
            const msg: string | undefined = body?.error?.message;
            if (msg) message = msg;
            // A permission/scope refusal (ErrorCode.FORBIDDEN) is a legitimate
            // "that isn't your data" answer, not a failure — render it as a
            // normal EVII message below, never the red error block.
            if (code === 'FORBIDDEN') {
              scopeRefusal = true;
            } else if (
              code === 'AI_BUDGET_EXHAUSTED'
              || code === 'AI_NOT_CONFIGURED'
              || code === 'RATE_LIMITED'
              || code === 'CONVERSATION_FORBIDDEN'
            ) {
              retryable = false;
            }
          } catch {
            // Body wasn't JSON or parser failed — keep the generic
            // message + retryable default.
          }
        }
        setMessages((prev) => [
          ...prev,
          scopeRefusal
            ? {
                kind: 'assistant',
                text: t('fallback.scopeRefusal'),
                timestamp: Date.now(),
                tool_calls: [],
                suggested_action: null,
              }
            : { kind: 'error', text: message, timestamp: Date.now(), retryable },
        ]);
        recordSnapshot(userId);
        return scopeRefusal ? t('fallback.scopeRefusal') : null;
      } finally {
        setSending(false);
      }
    },
    [chargerId, conversationId, sending, sessionId, setMessages, recordSnapshot, userId, t],
  );

  // ── Action confirmation (propose -> confirm -> execute, Actions Phase 0) ──
  // The latest drafted action still awaiting the driver's confirmation, for the
  // spoken-confirm path in voice mode (held in a ref so the stable voice loop
  // sees the current value).
  let pendingActionToken: string | null = null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m && m.kind === 'assistant' && m.pending_action) {
      pendingActionToken = m.pending_action.token;
      break;
    }
  }
  const pendingActionTokenRef = useRef<string | null>(null);
  useEffect(() => { pendingActionTokenRef.current = pendingActionToken; }, [pendingActionToken]);

  // Confirm or cancel a drafted action. On confirm the server runs the SAME
  // guarded path a human uses, as the session user; on cancel it discards the
  // draft. Either way appends EVII's result message. Returns the message (so the
  // voice path can speak it).
  const handleConfirmAction = useCallback(
    async (token: string, decision: 'confirm' | 'cancel'): Promise<string | null> => {
      // Optimistically drop the card from the proposing message so it can't be
      // double-submitted.
      setMessages((prev) => prev.map((m) =>
        m.kind === 'assistant' && m.pending_action?.token === token
          ? { ...m, pending_action: null }
          : m));
      try {
        const res = await confirmAssistantAction(conversationId, token, decision);
        setMessages((prev) => [
          ...prev,
          {
            kind: 'assistant', text: res.message, timestamp: Date.now(),
            tool_calls: [], suggested_action: null,
          },
        ]);
        recordSnapshot(userId);
        return res.message;
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            kind: 'error',
            text: t('error.confirmFailed'),
            timestamp: Date.now(), retryable: true,
          },
        ]);
        return null;
      }
    },
    [conversationId, setMessages, recordSnapshot, userId, t],
  );

  // ── Voice mode: hands-free browser STT -> handleSend -> Polly TTS -> repeat.
  //    Mirrors the admin EVII loop. The mic is live ONLY during the driver's
  //    turn: paused while the request is in flight and while the answer plays,
  //    so it never captures EVII's own voice (the echo problem). Both the spoken
  //    question and the answer go through handleSend, so they render as normal
  //    chat messages -- voice shares the transcript with text. ──
  const voiceActiveRef = useRef(voiceActive);
  useEffect(() => { voiceActiveRef.current = voiceActive; }, [voiceActive]);
  const voiceChoiceRef = useRef(voiceChoice);
  useEffect(() => { voiceChoiceRef.current = voiceChoice; }, [voiceChoice]);

  const stopSpeakRef = useRef<(() => void) | null>(null);
  const startListenRef = useRef<() => void>(() => {});
  const stopListenRef = useRef<() => void>(() => {});

  // Stop any in-flight playback (e.g. on toggle-off); resolves the pending speak().
  const stopAudio = useCallback(() => {
    stopSpeakRef.current?.();
    stopSpeakRef.current = null;
    setSpeaking(false);
  }, []);

  // Synthesize via Polly (driver's chosen voice) and play it AS IT STREAMS
  // (playSpeechStream) so EVII starts speaking after the first chunk, not the
  // whole clip. Resolves when playback ends, errors, or is stopped.
  const speak = useCallback(async (text: string): Promise<void> => {
    try {
      const response = await synthesizeDriverSpeech(
        text, voiceChoiceRef.current, normalizeLocale(i18n.language),
      );
      if (!voiceActiveRef.current) {
        try { await response.body?.cancel(); } catch { /* ignore */ }
        return; // toggled off while synthesizing
      }
      const playback = playSpeechStream(response);
      stopSpeakRef.current = playback.stop;
      setSpeaking(true);
      await playback.done;
    } catch {
      /* synth/playback failure: skip speaking this answer, keep the loop alive */
    } finally {
      stopSpeakRef.current = null;
      setSpeaking(false);
    }
  }, []);

  // Whether the voice loop may (re)start the recognizer RIGHT NOW. Read
  // from the LIVE stores, never from refs a dead closure could freeze:
  // after an await, the component may have unmounted (logout tears down
  // AppShell mid-turn) or the sheet may have closed (stopAudio resolving
  // a speak() synchronously) — a stale voiceActiveRef=true would restart
  // the mic with nothing left to ever stop it. unmountedRef covers the
  // torn-down case; the store reads cover closed-sheet and voice-off.
  const unmountedRef = useRef(false);
  const canRestartVoice = useCallback(() => (
    !unmountedRef.current
    && useEvieVoiceStore.getState().voiceActive
    && useEvieChatStore.getState().open
  ), []);

  // One full turn: send the spoken transcript through the normal flow, speak the
  // answer, then resume listening if still in voice mode.
  const handleVoiceTranscript = useCallback(async (transcript: string) => {
    if (!canRestartVoice()) return;
    // Spoken confirm/cancel when a drafted action awaits confirmation: a "yes"
    // confirms it, a "no" cancels it, anything else is a normal message.
    const token = pendingActionTokenRef.current;
    if (token) {
      const t = transcript.trim().toLowerCase();
      const yes = /^(yes|yeah|yep|confirm|confirmed|go ahead|do it|file it|send it|okay|ok)\b/.test(t);
      const no = /^(no|nope|cancel|don'?t|do not|stop|never ?mind)\b/.test(t);
      if (yes || no) {
        const msg = await handleConfirmAction(token, yes ? 'confirm' : 'cancel');
        if (msg && canRestartVoice()) await speak(msg);
        if (canRestartVoice()) startListenRef.current();
        return;
      }
    }
    const answer = await handleSend(transcript);
    if (!canRestartVoice()) return;
    if (answer) await speak(answer);
    if (canRestartVoice()) startListenRef.current();
  }, [handleSend, speak, handleConfirmAction, canRestartVoice]);

  // Listen in the driver's language (mic INPUT), mirroring the locale we already
  // send for the reply + Polly TTS. localeToBcp47 maps the app locale to the
  // region-qualified tag the Web Speech API needs; a language change re-inits the
  // recognizer on the next turn.
  const recognition = useSpeechRecognition(
    handleVoiceTranscript,
    localeToBcp47(i18n.language),
  );
  useEffect(() => {
    startListenRef.current = recognition.start;
    stopListenRef.current = recognition.stop;
  }, [recognition.start, recognition.stop]);
  const recognitionSupported = recognition.supported;

  // Enter/exit voice mode: start listening on, stop the mic + any audio on off.
  // Gated on isOpen too: this component stays mounted while the sheet is
  // closed (BottomSheet only unmounts its DOM), and voiceActive is a global
  // store value that can arrive stale-true from a previous host (the
  // always-open /assistant sheet and EVII's nav button both unmount without
  // closing). Without the gate, the start-charge pages briefly started the
  // mic on mount behind a closed sheet. The mic may only run while the
  // sheet is actually open.
  useEffect(() => {
    if (!recognitionSupported) return;
    if (voiceActive && isOpen) startListenRef.current();
    else { stopListenRef.current(); stopAudio(); }
  }, [voiceActive, isOpen, recognitionSupported, stopAudio]);

  // Closing the sheet ends voice mode -- never leave the mic live behind a
  // closed sheet.
  useEffect(() => {
    if (!isOpen && voiceActive) setVoiceActive(false);
  }, [isOpen, voiceActive, setVoiceActive]);

  // Auto-start voice when the sheet OPENS, if the user's auto-start pref is on
  // (the default). Fires once per open (a ref tracks the transition), only when
  // recognition is supported; setting voiceActive triggers the effect above,
  // which starts the mic (prompting for permission the first time).
  const autostartedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) { autostartedRef.current = false; return; }
    if (autostartedRef.current) return;
    autostartedRef.current = true;
    if (recognitionSupported && micAutostart && !voiceActiveRef.current) {
      setVoiceActive(true);
    }
  }, [isOpen, recognitionSupported, micAutostart, setVoiceActive]);

  // Final safety: stop the mic + any audio on unmount, mark the closure
  // dead so no in-flight voice turn can restart the recognizer after
  // teardown, and reset the global voice flag so nothing hands a stale
  // true to a future mount.
  useEffect(() => () => {
    unmountedRef.current = true;
    stopListenRef.current();
    stopAudio();
    setVoiceActive(false);
  }, [stopAudio, setVoiceActive]);

  // Receipt download: EVII's prepare_my_receipt tool returns a validated
  // range on the envelope; tapping the button fetches the authenticated
  // PDF (bearer token via the shared `api` client) for that exact range,
  // stamped with the browser timezone for the PDF cover note. On failure
  // we surface an inline error bubble rather than a silent no-op.
  const handleDownloadReceipt = useCallback(
    async (range: { from: string; to: string }) => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
        await downloadSessionsReportPdf({ from: range.from, to: range.to, tz });
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            kind: 'error',
            text: t('error.receiptFailed'),
            timestamp: Date.now(),
            retryable: true,
          },
        ]);
      }
    },
    [t],
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void handleSend(input);
  };

  // Quick-action shortcuts: a "Check status" suggestion as the
  // first prompt the driver can tap without typing. Disappears as
  // soon as any message has been exchanged.
  const showQuickActions = messages.length <= 1 && !sending;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="EVII™"
      // Header orb: same asset + reduced-motion poster as the bubble
      // avatars, sized to the 26px header line (admin's EvieChat header
      // carries the same orb-beside-wordmark treatment).
      titleIcon={<EvieOrb size={22} />}
      headerAction={
        <button
          type="button"
          className={styles.historyBtn}
          onClick={() => setHistoryOpen((v) => !v)}
          aria-label={t('history.ariaLabel')}
          aria-expanded={historyOpen}
        >
          <MoreVertical size={18} />
        </button>
      }
      backdropTone="brand"
      glass
    >
      <div className={styles.container}>
        {historyOpen && (
          <div className={styles.historyPanel}>
            <div className={styles.historyHead}>
              <History size={13} /> <span>{t('history.recentTitle')}</span>
            </div>
            {recentConversations.length === 0 ? (
              <p className={styles.historyEmpty}>{t('history.empty')}</p>
            ) : (
              <div className={styles.historyList}>
                {recentConversations.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={styles.historyRow}
                    // Disabled mid-send: swapping the active thread while a
                    // reply is in flight would splice that reply into the
                    // resumed conversation and persist the corrupted merge.
                    disabled={sending}
                    onClick={() => {
                      // Load the stored thread as the active conversation.
                      // Never touches voice state — the mic follows the
                      // sheet-open gate and the per-user toggle only.
                      activateConversation(userId, c.id);
                      setHistoryOpen(false);
                    }}
                  >
                    <span className={styles.historyPreview}>
                      {c.first_user_text.length > 52
                        ? `${c.first_user_text.slice(0, 52)}…`
                        : c.first_user_text}
                    </span>
                    <span className={styles.historyTime}>
                      {c.id === conversationId ? `${t('history.current')} · ` : ''}
                      {relativeTime(c.at)}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {suggestedPrompts.length > 0 && (
              <>
                <div className={styles.historyHead}><span>{t('history.askAgain')}</span></div>
                <div className={styles.suggestChips}>
                  {suggestedPrompts.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={styles.suggestChip}
                      title={p}
                      disabled={sending}
                      onClick={() => {
                        setHistoryOpen(false);
                        void handleSend(p);
                      }}
                    >
                      {p.length > 44 ? `${p.slice(0, 44)}…` : p}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        <div ref={scrollRef} className={styles.transcript} aria-live="polite">
          {messages.map((m, idx) => (
            <MessageRow
              key={`${m.timestamp}-${idx}`}
              entry={m}
              onContactSupport={onContactSupport}
              onDownloadReceipt={handleDownloadReceipt}
              onNavigate={onNavigate}
              onCloseSheet={onClose}
              onConfirmAction={handleConfirmAction}
            />
          ))}
          {sending && (
            <div className={styles.typing}>
              <Loader2 size={14} className={styles.spin} />
              <span>{t('status.thinking')}</span>
            </div>
          )}
        </div>

        {showQuickActions && (
          <div className={styles.quickActions}>
            {quickActions.map((qa) => (
              <button
                key={qa.label}
                type="button"
                className={styles.quickAction}
                onClick={() => void handleSend(qa.prompt)}
              >
                {qa.label}
              </button>
            ))}
          </div>
        )}

        {voiceActive && (
          <div className={styles.voiceBar}>
            <span
              className={`${styles.voiceDot} ${
                speaking
                  ? styles.voiceDotSpeaking
                  : recognition.listening
                    ? styles.voiceDotListening
                    : ''
              }`}
            />
            <span className={styles.voiceStatus}>
              {speaking
                ? t('voice.speaking')
                : sending
                  ? t('status.thinking')
                  : recognition.listening
                    ? (recognition.interimText || t('voice.listening'))
                    : t('voice.on')}
            </span>
            <button
              type="button"
              className={styles.voiceStop}
              onClick={() => setVoiceActive(false)}
            >
              {t('voice.stop')}
            </button>
          </div>
        )}

        <form onSubmit={onSubmit} className={styles.composer}>
          {/* Mic toggle — present when the browser supports speech recognition
              (hidden e.g. on Firefox). Runs the hands-free voice loop. */}
          {recognitionSupported && (
            <button
              type="button"
              className={`${styles.micBtn} ${voiceActive ? styles.micBtnActive : ''}`}
              onClick={toggleVoice}
              aria-label={voiceActive ? t('mic.turnOff') : t('mic.turnOn')}
              aria-pressed={voiceActive}
              title={voiceActive ? t('mic.titleOn') : t('mic.titleOff')}
            >
              <Mic size={16} />
            </button>
          )}
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t(`placeholder.${surface}`)}
            maxLength={2000}
            disabled={sending}
            className={styles.composerInput}
            aria-label={t('composer.inputAria')}
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className={styles.composerSend}
            aria-label={t('composer.send')}
          >
            <Send size={16} />
          </button>
        </form>
        <p className={styles.scopeHint}>{t(`scopeHint.${surface}`)}</p>
      </div>
    </BottomSheet>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function MessageRow({
  entry,
  onContactSupport,
  onDownloadReceipt,
  onNavigate,
  onCloseSheet,
  onConfirmAction,
}: {
  entry: MessageEntry;
  onContactSupport: () => void;
  onDownloadReceipt: (range: { from: string; to: string }) => void;
  onNavigate: (path: string) => void;
  /** Closes the sheet after an EVII navigation tap — the store-mounted
   *  sheet no longer unmounts on route change. */
  onCloseSheet: () => void;
  onConfirmAction: (token: string, decision: 'confirm' | 'cancel') => void;
}) {
  const { t } = useTranslation('driverAssist');
  if (entry.kind === 'user') {
    return (
      <div className={`${styles.bubble} ${styles.bubbleUser}`}>
        <p className={styles.bubbleText}>{entry.text}</p>
      </div>
    );
  }
  if (entry.kind === 'error') {
    return (
      <div className={`${styles.bubble} ${styles.bubbleError}`}>
        <div className={styles.bubbleHeader}>
          <AlertTriangle size={14} />
          <span>{entry.retryable ? t('error.tryAgain') : t('error.unavailable')}</span>
        </div>
        <p className={styles.bubbleText}>{entry.text}</p>
        <button type="button" className={styles.supportBtn} onClick={onContactSupport}>
          <LifeBuoy size={14} /> {t('support.contact')}
        </button>
      </div>
    );
  }

  // Assistant. The server flags an out-of-scope decline with
  // suggested_action: 'contact_support'; show the support button off
  // that flag alone (no client-side text matching).
  const shouldShowSupport = entry.suggested_action === 'contact_support';
  // const (not let) so TS preserves the truthiness narrowing into the
  // onClick closure below without a non-null assertion.
  const receiptRange = entry.receipt_download ?? null;
  // const so TS keeps the truthiness narrowing inside the onClick closure.
  const nav = entry.navigation ?? null;
  // Drafted action awaiting the driver's explicit confirmation (Actions Phase 0).
  const pendingAction = entry.pending_action ?? null;

  return (
    <div className={`${styles.bubble} ${styles.bubbleAssistant}`}>
      <div className={styles.bubbleHeader}>
        {/* EVII orb (animated WebP, TokenSpin pattern), at the bubble
            avatar size. Renders once evie-orb.webp lands in public/. */}
        <EvieOrb size={14} />
        <span>EVII™</span>
      </div>
      {entry.tool_calls.length > 0 && (
        <div className={styles.toolList}>
          {entry.tool_calls.map((tc, i) => (
            <ToolStatusPill key={i} call={tc} />
          ))}
        </div>
      )}
      <MarkdownText className={styles.bubbleText} text={entry.text} />
      {shouldShowSupport && (
        <button type="button" className={styles.supportBtn} onClick={onContactSupport}>
          <LifeBuoy size={14} /> {t('support.contact')}
        </button>
      )}
      {receiptRange && (
        <button
          type="button"
          className={styles.supportBtn}
          onClick={() => onDownloadReceipt(receiptRange)}
        >
          <Download size={14} /> {t('receipt.download')}
        </button>
      )}
      {/* Offer-only: this is a tap target, never auto-navigated on render.
          The sheet is a single AppShell-level instance now, so a route
          change no longer unmounts it — navigating closes the sheet
          explicitly (which also stops the mic via the isOpen gate). The
          conversation stays in the evieChat store; reopening from the
          header resumes it. */}
      {nav && (
        <button
          type="button"
          className={styles.supportBtn}
          onClick={() => {
            onCloseSheet();
            // '/tour' is the guided-tour sentinel (NAV_ALLOWLIST take_tour),
            // not a real route — open the tour instead of navigating.
            if (nav.path === '/tour') { useTourStore.getState().open(); return; }
            onNavigate(nav.path);
          }}
        >
          <ArrowRight size={14} /> {nav.label}
        </button>
      )}
      {/* Confirm-before-execute card (EVII Actions). EVII proposed an action; it
          runs only when the driver explicitly confirms here. The driver sees
          exactly what will change before confirming. */}
      {pendingAction && (
        <div className={styles.confirmCard}>
          <div className={styles.confirmHead}>
            <Flag size={13} /> <span>{pendingAction.title}</span>
          </div>
          <p className={styles.confirmSummary}>{pendingAction.summary}</p>
          <p className={styles.confirmDetail}>{pendingAction.detail}</p>
          <div className={styles.confirmActions}>
            <button
              type="button"
              className={styles.confirmYes}
              onClick={() => onConfirmAction(pendingAction.token, 'confirm')}
            >
              <CheckCircle2 size={13} /> {pendingAction.confirmLabel ?? t('confirm.confirm')}
            </button>
            <button
              type="button"
              className={styles.confirmNo}
              onClick={() => onConfirmAction(pendingAction.token, 'cancel')}
            >
              {t('confirm.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ToolStatusPill({ call }: { call: AssistantToolCall }) {
  const { t } = useTranslation('driverAssist');
  // Default label is the raw tool name (server-provided identifier, not
  // translated); the known tools map to a localized status label.
  let label = call.name;
  let icon = <CheckCircle2 size={12} />;
  if (call.name === 'get_charger_status_for_session') {
    label = t('tool.checkedStatus');
  } else if (call.name === 'trigger_soft_reset') {
    label = t('tool.softReset');
    icon = <RotateCcw size={12} />;
  } else if (call.name === 'trigger_hard_reset') {
    label = t('tool.hardReset');
    icon = <RotateCcw size={12} />;
  }
  // Crude error detection: result_summary starts with '{"error"'.
  const isError = call.result_summary.startsWith('{"error"');
  return (
    <div
      className={`${styles.toolPill} ${isError ? styles.toolPillError : ''}`}
      title={call.result_summary}
    >
      {isError ? <AlertTriangle size={12} /> : icon}
      <span>{label}{isError ? ` (${t('tool.failed')})` : ''}</span>
    </div>
  );
}

// ── FAB launcher ────────────────────────────────────────────────────────
//
// Co-located with the sheet because they're always paired. The
// host page (PreSessionPage) renders `<DriverAssistFab onClick={...}/>`
// + `<DriverAssistSheet isOpen={...} />` and manages the open state.

export function DriverAssistFab({
  onClick,
  hidden,
  variant = 'floating',
}: {
  onClick: () => void;
  hidden?: boolean;
  /** Kept for API parity with the sheet; the launcher copy is the same across
   *  surfaces, so it no longer selects the text. */
  surface?: Surface;
  /** 'floating' pins the launcher fixed bottom-right (default); 'inline'
   *  renders it as a normal-flow button the parent positions (the charging
   *  screen places it in the header, opposite the "Charging" title). */
  variant?: 'floating' | 'inline';
}) {
  const { t } = useTranslation('driverAssist');
  if (hidden) return null;
  return (
    <button
      type="button"
      className={variant === 'inline' ? `${styles.fab} ${styles.fabInline}` : styles.fab}
      onClick={onClick}
      aria-label={t('fabAria')}
    >
      {/* EVII orb (animated WebP, TokenSpin pattern), sized larger as the
          always-on launcher's brand mark. EvieOrb honours
          prefers-reduced-motion by swapping to the static poster, so the
          persistent button isn't a battery drain. Renders once
          evie-orb.webp lands in public/. */}
      <EvieOrb size={20} />
      <span>{t('fabLabel')}</span>
    </button>
  );
}
